/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { X, ZoomIn } from "lucide-react";
import { BENTO_PHOTOS, MARQUEE_PHOTOS } from "@/lib/photos";

// 12-col grid on desktop, simpler 2-col stack on mobile.
const BENTO_LAYOUT = [
  "col-span-2 sm:col-span-2 md:col-span-8 row-span-2",  // 0 big top-left (mobile: full width tall)
  "col-span-1 sm:col-span-1 md:col-span-4 row-span-1",  // 1
  "col-span-1 sm:col-span-1 md:col-span-4 row-span-1",  // 2
  "col-span-1 sm:col-span-1 md:col-span-4 row-span-1",  // 3
  "col-span-1 sm:col-span-1 md:col-span-4 row-span-1",  // 4
  "col-span-2 sm:col-span-2 md:col-span-4 row-span-1",  // 5
  "col-span-2 sm:col-span-2 md:col-span-8 row-span-2",  // 6 big bottom
  "col-span-2 sm:col-span-2 md:col-span-4 row-span-2",  // 7
];

export default function Gallery() {
  const [open, setOpen] = useState(null);

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-[#050505] overflow-hidden" id="galerie" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#FFD60A]" />
              <span className="font-mono text-xs tracking-[0.4em] text-[#FFD60A] uppercase">Coulisses</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white uppercase leading-[0.95]">
              Dans nos <span className="text-[#FFD60A]">centres</span>, tous les jours
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zinc-400 leading-relaxed">
              Ferrari, Lamborghini, Porsche, BMW M, Mercedes-AMG, GT-R, Mustang… Voici une <span className="text-white font-semibold">sélection des véhicules</span> qui passent chez Horsepower. Pas de photo de stock — du vrai terrain.
            </p>
          </div>
        </div>

        {/* Bento mosaic - 2 cols mobile, 12 cols desktop */}
        <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[140px] sm:auto-rows-[180px] md:auto-rows-[220px] gap-2 md:gap-3">
          {BENTO_PHOTOS.map((p, i) => (
            <motion.button
              key={i}
              onClick={() => setOpen(p)}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              className={`group relative overflow-hidden bg-zinc-900 ${BENTO_LAYOUT[i]}`}
              data-testid={`bento-photo-${i}`}
            >
              <img
                src={p.src}
                alt={p.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-[#FFD60A] transition-colors" />
              <div className="absolute top-3 right-3 w-9 h-9 bg-black/70 backdrop-blur border border-white/20 group-hover:bg-[#FFD60A] group-hover:border-[#FFD60A] flex items-center justify-center transition-all">
                <ZoomIn size={14} className="text-white group-hover:text-black transition-colors" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD60A]">{p.tag}</div>
                <div className="text-sm text-white font-semibold mt-0.5 line-clamp-1">{p.label}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Marquee strip - infinite horizontal scroll */}
      <div className="mt-16 relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none" />
        <div className="flex overflow-hidden">
          <div className="flex gap-3 animate-marquee shrink-0">
            {[...MARQUEE_PHOTOS, ...MARQUEE_PHOTOS].map((p, i) => (
              <button
                key={i}
                onClick={() => setOpen(p)}
                className="relative shrink-0 w-64 h-40 overflow-hidden group bg-zinc-900"
                data-testid={`marquee-photo-${i}`}
              >
                <img src={p.src} alt={p.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8"
          onClick={() => setOpen(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setOpen(null)}
            className="absolute top-6 right-6 w-12 h-12 border border-white/30 hover:border-[#FFD60A] hover:bg-[#FFD60A] hover:text-black flex items-center justify-center text-white transition-all z-10"
            data-testid="gallery-lightbox-close"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={open.src} alt={open.label} className="w-full h-auto max-h-[85vh] object-contain border border-zinc-800" />
            <div className="mt-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-zinc-800" />
              {open.tag && <div className="font-mono text-xs uppercase tracking-widest text-[#FFD60A]">{open.tag}</div>}
              <div className="text-white font-semibold">{open.label}</div>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
